import { lazy } from "react";
import { matchPath } from "react-router-dom";
import { DIET_ROUTES } from "../../features/diet/core/constants/routes";
import { WORKOUT_ROUTES } from "../../features/workouts/core/constants/routes";

// Lazily loaded for the same reason routes.jsx does it: these editors are large
// and most sessions never open one.
const AssignDietPlanPage = lazy(() =>
  import("../../features/diet/workflow/plan/modify/AssignDietPlanPage")
);
const DietTemplateListPage = lazy(() =>
  import("../../features/diet/workflow/template/list/TemplateListPage")
);
const DietTemplateEditPage = lazy(() =>
  import("../../features/diet/workflow/template/modify/TemplateEditPage")
);
const DietTemplateCreatePage = lazy(() =>
  import("../../features/diet/workflow/template/modify/TemplateCreatePage")
);
const WorkoutPlanPage = lazy(() =>
  import("../../features/workouts/workflow/plan/modify/WorkoutPlanPage")
);
const WorkoutTemplateListPage = lazy(() =>
  import("../../features/workouts/workflow/template/list/TemplateListPage")
);
const WorkoutTemplateEditPage = lazy(() =>
  import("../../features/workouts/workflow/template/modify/TemplateEditPage")
);
const WorkoutTemplateCreatePage = lazy(() =>
  import("../../features/workouts/workflow/template/modify/TemplateCreatePage")
);
// Opening an existing plan to edit it. These live on /dashboard/:id/... rather
// than under the feature's own prefix, but they belong to the same flow.
const DietPlanDetailPage = lazy(() =>
  import("../../features/diet/workflow/plan/view/DietPlanDetailPage")
);
const WorkoutDurationDetailPage = lazy(() =>
  import("../../features/workouts/workflow/profile/view/WorkoutDurationDetailPage")
);

/** Edit-an-existing-plan routes, defined in App.js rather than a feature routes file. */
const PLAN_DETAIL_PATHS = {
  DIET: "/dashboard/:id/diet-plan/:planId",
  WORKOUT: "/dashboard/:id/workout-plan/:durationId",
};

/**
 * Route-level flows that can render inside a panel instead of navigating.
 *
 * Each entry lists EVERY path the flow can move through, because these pages
 * re-drive themselves by navigating to their own route (and, for workouts, from
 * the template list into the plan editor). Inside the host's nested router
 * those hops resolve here and keep working untouched; anything NOT listed is
 * treated as leaving the flow, which is how "save and return" closes it.
 *
 * `provider` mirrors the Shell wrapper from each feature's routes.jsx —
 * DietDataProvider / WorkoutDataProvider — which the pages require. Loading it
 * lazily keeps the provider out of the main bundle too.
 */
export const EMBEDDABLE_FLOWS = [
  {
    id: "diet-plan",
    title: "Diet plan",
    loadProvider: () =>
      import("../../features/diet/core/providers/DietDataProvider").then(
        (m) => m.DietDataProvider
      ),
    routes: [
      { path: DIET_ROUTES.PLANS_EDITOR, element: AssignDietPlanPage },
      // The template pages are reachable from the plan editor's source picker
      // (pick a template, clone it, create a new one) and navigate back to the
      // plan editor when done — so they belong to this flow. Leaving any of
      // them out would close the panel mid-flow.
      { path: DIET_ROUTES.TEMPLATES_LIST, element: DietTemplateListPage },
      { path: DIET_ROUTES.TEMPLATES_NEW, element: DietTemplateCreatePage },
      { path: DIET_ROUTES.TEMPLATES_DETAIL, element: DietTemplateEditPage },
      { path: PLAN_DETAIL_PATHS.DIET, element: DietPlanDetailPage },
    ],
  },
  {
    id: "workout-plan",
    title: "Workout plan",
    loadProvider: () =>
      import("../../features/workouts/core/providers/WorkoutDataProvider").then(
        (m) => m.WorkoutDataProvider
      ),
    routes: [
      { path: WORKOUT_ROUTES.PLAN_EDITOR, element: WorkoutPlanPage },
      { path: WORKOUT_ROUTES.TEMPLATES_LIST, element: WorkoutTemplateListPage },
      { path: WORKOUT_ROUTES.TEMPLATES_NEW, element: WorkoutTemplateCreatePage },
      { path: WORKOUT_ROUTES.TEMPLATES_DETAIL, element: WorkoutTemplateEditPage },
      { path: PLAN_DETAIL_PATHS.WORKOUT, element: WorkoutDurationDetailPage },
    ],
  },
];

/**
 * Split a `navigate` destination into its pathname and search parts.
 * @param {string} to
 * @returns {{pathname: string, search: string}}
 */
export function splitPath(to) {
  const [pathname, search = ""] = String(to).split("?");
  return { pathname, search: search ? `?${search}` : "" };
}

/**
 * The flow that owns a destination, or null when nothing can host it.
 * @param {string} to
 * @returns {(typeof EMBEDDABLE_FLOWS)[number]|null}
 */
export function findFlowFor(to) {
  const { pathname } = splitPath(to);
  return (
    EMBEDDABLE_FLOWS.find((flow) =>
      flow.routes.some((route) => matchPath(route.path, pathname))
    ) || null
  );
}

/**
 * Whether a destination is still inside the given flow. Used by the host to
 * tell a self-navigation (stay) from an exit navigation (close).
 *
 * @param {(typeof EMBEDDABLE_FLOWS)[number]} flow
 * @param {string} pathname
 * @returns {boolean}
 */
export function isInsideFlow(flow, pathname) {
  return flow.routes.some((route) => matchPath(route.path, pathname));
}

/**
 * The route PATTERN a pathname matched within a flow, or null.
 *
 * The host needs the pattern (not the concrete path) to run the same
 * `hasRouteAccess` check `PrivateRoute` would have applied — embedding skips
 * `PrivateRoute`, so without this an inline render could reach a page the
 * coach isn't allowed to open by URL.
 *
 * @param {(typeof EMBEDDABLE_FLOWS)[number]} flow
 * @param {string} pathname
 * @returns {string|null}
 */
export function matchedPatternInFlow(flow, pathname) {
  const route = flow.routes.find((r) => matchPath(r.path, pathname));
  return route ? route.path : null;
}
